import type { AdminQuoteDetail } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { canCreateQuoteRevisionDraft, canEditQuoteDraft, canRejectQuote } from '@/entities/quote';
import { QuoteDraftEditor, RejectQuoteForm, RevisionDraftButton } from '@/features/quote';
import { Button, ContentCard } from '@/shared/ui';
import { QuoteAssetsSection } from './quote-assets-section';
import { QuoteContactSection } from './quote-contact-section';
import { QuoteDesignContents } from './quote-design-contents';
import { QuoteDesignPreview } from './quote-design-preview';
import { QuoteIssuedVersions } from './quote-issued-versions';
import { QuoteNotesSection } from './quote-notes-section';
import { QuoteOrderSection } from './quote-order-section';
import { QuoteRequestInfo } from './quote-request-info';
import { QuoteRevisionHistory } from './quote-revision-history';

interface QuoteDetailContentProps {
  readonly quote: AdminQuoteDetail;
  /** 충돌 뒤 "최신 초안 다시 불러오기" 횟수 — 초안 id 와 함께 편집기의 key 가 된다. */
  readonly editorEpoch: number;
  /** 충돌 뒤 운영자가 "최신 초안 다시 불러오기"를 고르면 부른다 — 패널이 상세를 다시 불러오고 횟수를 올린다. */
  readonly onReloadLatest: () => void;
  /** 배경 재조회 실패 안내 — 있으면 맨 위에 노란 띠와 다시 시도 버튼을 둔다. */
  readonly error: string | null;
  readonly onReload: () => void;
}

/**
 * 견적 상세 본문(견적 설계 §6-2~6-7) — 머리말(견적 번호·상태 배지·뒤로)은 패널이 그린다. 넓은 화면은 두 칸: 왼쪽에 반려·거절 사유,
 * 주문 영역, 요청 내용·텍스트·폰트·잉크·사용 이미지·메모·수정 요청 이력·초안 편집기(또는 재발행 초안 버튼)·반려·발행본,
 * 오른쪽에 고객 연락처·디자인 사본.
 */
export const QuoteDetailContent = ({
  quote,
  editorEpoch,
  onReloadLatest,
  error,
  onReload,
}: QuoteDetailContentProps): ReactElement => {
  const showsEditor = quote.draft !== null && canEditQuoteDraft(quote.status);
  const showsRevisionButton = quote.draft === null && canCreateQuoteRevisionDraft(quote.status);
  // 디자인 없는 일반 견적(인쇄 없는 상품)은 디자인 사본·고객 이미지가 없다 — 그 카드들을 그리지 않는다.
  const { designBody, designName, thumbnailUrl } = quote.design;

  return (
    <div className="flex flex-col gap-6">
      {error ? (
        <div
          role="status"
          aria-label="최신 내용 불러오기 실패"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <p>{error}</p>
          <Button type="button" variant="outline" size="sm" onClick={onReload}>
            다시 시도
          </Button>
        </div>
      ) : null}

      {quote.rejectedReason !== null ? (
        <ContentCard title="반려 사유" className="border-destructive/30">
          <p className="text-sm whitespace-pre-wrap wrap-anywhere">{quote.rejectedReason}</p>
        </ContentCard>
      ) : null}

      {quote.declinedReason !== null ? (
        <ContentCard title="고객 거절 사유">
          <p className="text-sm whitespace-pre-wrap wrap-anywhere">{quote.declinedReason}</p>
        </ContentCard>
      ) : null}

      {quote.status === 'ORDERED' ? (
        <QuoteOrderSection
          orderId={quote.orderId}
          orderDisplayStatus={quote.orderDisplayStatus}
          payment={quote.payment}
        />
      ) : null}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-6">
          <QuoteRequestInfo detail={quote} />
          {designBody === null ? null : (
            <>
              <QuoteDesignContents designBody={designBody} />
              <QuoteAssetsSection quoteRequestId={quote.id} assetIds={quote.assetIds} />
            </>
          )}
          <div className="grid gap-6 xl:grid-cols-2">
            <QuoteNotesSection quoteRequestId={quote.id} notes={quote.notes} />
            <QuoteRevisionHistory
              requests={quote.revisionRequests}
              versions={quote.issuedVersions}
            />
          </div>

          {showsEditor && quote.draft !== null ? (
            <QuoteDraftEditor
              key={`${quote.draft.versionId}:${editorEpoch}`}
              quoteRequestId={quote.id}
              draft={quote.draft}
              destinations={quote.destinations}
              onReloadLatest={onReloadLatest}
              guest={quote.guest}
            />
          ) : null}
          {showsRevisionButton ? (
            <ContentCard title="재발행">
              <RevisionDraftButton quoteRequestId={quote.id} />
            </ContentCard>
          ) : null}

          {canRejectQuote(quote.status) ? <RejectQuoteForm quoteRequestId={quote.id} /> : null}

          <QuoteIssuedVersions
            versions={quote.issuedVersions}
            quoteNumber={quote.quoteNumber}
            requestedAt={quote.createdAt}
            destinations={quote.destinations}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <QuoteContactSection
            quoteRequestId={quote.id}
            email={quote.userEmail}
            guest={quote.guest}
          />
          {designBody === null ? null : (
            <QuoteDesignPreview
              quoteRequestId={quote.id}
              thumbnailUrl={thumbnailUrl}
              designName={designName ?? ''}
              designBody={designBody}
            />
          )}
        </div>
      </div>
    </div>
  );
};
